import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../api/client.js';
import { DataTable, type ColumnDef } from '../../components/ui/DataTable.js';
import { Button } from '../../components/ui/Button.js';
import { Modal, ConfirmDialog } from '../../components/ui/Modal.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { useTableSelection } from '../../hooks/useTableSelection.js';
import { Plus, Trash2 } from 'lucide-react';

interface TransactionItem {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: 'INCOME' | 'EXPENSE';
  status: 'PAID' | 'PENDING';
  source: string;
  linkedEventId: string | null;
  linkedProjectId: string | null;
  dateIsApproximate: boolean;
}

interface Totals {
  income: number;
  expense: number;
  balance: number;
  pendingIncome?: number;
  pendingExpense?: number;
  projectedBalance?: number;
}

interface TransactionResponse {
  transactions: TransactionItem[];
  totals: Totals;
  hasMore: boolean;
}

interface Filters {
  from: string;
  to: string;
  type: '' | 'INCOME' | 'EXPENSE';
  showPending: boolean;
  source: string;
}

const SOURCE_LABELS: Record<string, string> = {
  event_deposit: 'Acconto evento',
  event_payment: 'Acconto evento',
  event_balance: 'Saldo evento',
  event_expense: 'Spesa evento',
  event_team_fee: 'Compenso team',
  project_revenue: 'Ricavo progetto',
  project_expense: 'Spesa progetto',
  project_collab_fee: 'Compenso progetto',
  performance_payment: 'Prestazione',
  fixed_cost: 'Costo fisso',
  manual: 'Manuale',
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT');
}

function TransactionFormModal({
  open, onClose, editing, onSave,
}: {
  open: boolean; onClose: () => void;
  editing: Partial<TransactionItem> | null;
  onSave: (data: Record<string, unknown>) => Promise<void>;
}) {
  const [form, setForm] = useState({
    date: editing?.date ? editing.date.slice(0, 10) : new Date().toISOString().slice(0, 10),
    description: editing?.description || '',
    amount: editing?.amount ? String(Math.abs(editing.amount)) : '',
    isIncome: editing?.type === 'INCOME' || editing?.amount == null || (editing?.amount ?? 0) > 0,
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open) {
      setForm({
        date: editing?.date ? editing.date.slice(0, 10) : new Date().toISOString().slice(0, 10),
        description: editing?.description || '',
        amount: editing?.amount ? String(Math.abs(editing.amount)) : '',
        isIncome: editing?.type === 'INCOME' || editing?.amount == null || (editing?.amount ?? 0) > 0,
        notes: '',
      });
    }
  }, [editing, open]);

  const handleSubmit = async () => {
    if (!form.description || !form.amount) return;
    setSaving(true);
    try {
      await onSave({
        date: new Date(form.date).toISOString(),
        description: form.description,
        amount: Number(form.amount) * (form.isIncome ? 1 : -1),
        notes: form.notes || undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title={editing ? 'Modifica movimento' : 'Nuovo movimento'}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', minWidth: 400 }}>
        <div>
          <label style={{ fontSize: 11, color: 'var(--color-text-faint)', fontWeight: 600, display: 'block', marginBottom: 4 }}>Data</label>
          <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
            style={{ width: '100%', padding: '0.5rem', background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)', color: 'var(--color-text)', fontSize: 'var(--text-sm)' }} />
        </div>
        <div>
          <label style={{ fontSize: 11, color: 'var(--color-text-faint)', fontWeight: 600, display: 'block', marginBottom: 4 }}>Descrizione</label>
          <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="es. Rimborso trasferta Milano"
            style={{ width: '100%', padding: '0.5rem', background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)', color: 'var(--color-text)', fontSize: 'var(--text-sm)' }} />
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, color: 'var(--color-text-faint)', fontWeight: 600, display: 'block', marginBottom: 4 }}>Importo</label>
            <input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem', background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)', color: 'var(--color-text)', fontSize: 'var(--text-sm)' }} />
          </div>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <Button size="sm" variant={form.isIncome ? 'primary' : 'secondary'} onClick={() => setForm(f => ({ ...f, isIncome: true }))}>Entrata</Button>
            <Button size="sm" variant={!form.isIncome ? 'primary' : 'secondary'} onClick={() => setForm(f => ({ ...f, isIncome: false }))}>Uscita</Button>
          </div>
        </div>
        <div>
          <label style={{ fontSize: 11, color: 'var(--color-text-faint)', fontWeight: 600, display: 'block', marginBottom: 4 }}>Note</label>
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
            style={{ width: '100%', padding: '0.5rem', background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)', color: 'var(--color-text)', fontSize: 'var(--text-sm)', resize: 'vertical' }} />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
          <Button variant="secondary" size="sm" onClick={onClose}>Annulla</Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} loading={saving}>
            {editing ? 'Salva' : 'Crea'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function TimeRangeFilter({ from, to, onApply }: { from: string; to: string; onApply: (from: string, to: string) => void }) {
  const [localFrom, setLocalFrom] = useState(from);
  const [localTo, setLocalTo] = useState(to);

  React.useEffect(() => { setLocalFrom(from); setLocalTo(to); }, [from, to]);

  return (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
      <input type="date" value={localFrom} onChange={e => setLocalFrom(e.target.value)}
        style={{ padding: '0.35rem 0.5rem', background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)', color: 'var(--color-text)', fontSize: 'var(--text-xs)' }} />
      <span style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>—</span>
      <input type="date" value={localTo} onChange={e => setLocalTo(e.target.value)}
        style={{ padding: '0.35rem 0.5rem', background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)', color: 'var(--color-text)', fontSize: 'var(--text-xs)' }} />
      <Button size="sm" variant="secondary" onClick={() => onApply(localFrom, localTo)}>Applica</Button>
    </div>
  );
}

export default function FinanceMovimentiPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState<Filters>({
    from: `${new Date().getFullYear()}-01-01`,
    to: new Date().toISOString().slice(0, 10),
    type: '',
    showPending: false,
    source: '',
  });

  const resetKey = `${filters.from}|${filters.to}|${filters.type}|${filters.showPending}|${filters.source}|${offset}`;
  const sel = useTableSelection(resetKey);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<TransactionItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    p.set('from', filters.from);
    p.set('to', filters.to);
    p.set('limit', '200');
    p.set('offset', String(offset));
    p.set('sort', 'date');
    p.set('order', 'desc');
    if (filters.type) p.set('type', filters.type);
    if (filters.showPending) p.set('showPending', 'true');
    if (filters.source) p.set('source', filters.source);
    return p.toString();
  }, [filters, offset]);

  const { data, isLoading } = useQuery<TransactionResponse>({
    queryKey: ['finance-transactions', filters.from, filters.to, filters.type, filters.showPending, filters.source, offset],
    queryFn: () => apiGet<TransactionResponse>(`/finance/transactions?${queryParams}`),
  });

  const totals = data?.totals;
  const hasMore = data?.hasMore ?? false;
  const transactions = data?.transactions ?? [];

  const handleFiltersChange = useCallback((patch: Partial<Filters>) => {
    setFilters(f => ({ ...f, ...patch }));
    setOffset(0);
  }, []);

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/finance/transactions', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['finance-transactions'] }); setShowForm(false); toast.success('Movimento creato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) => apiPatch(`/finance/transactions/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['finance-transactions'] }); setShowForm(false); setEditing(null); toast.success('Movimento aggiornato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/finance/transactions/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['finance-transactions'] }); setDeleteTarget(null); toast.success('Movimento eliminato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleSave = async (formData: Record<string, unknown>) => {
    if (editing) {
      await updateMutation.mutateAsync({ ...formData, id: editing.id.replace('manual_', '') });
    } else {
      await createMutation.mutateAsync(formData);
    }
  };

  const handleRowClick = useCallback((row: TransactionItem) => {
    if (row.source === 'manual' || row.source === 'fixed_cost') return;
    if (row.linkedEventId) {
      navigate(`/events/${row.linkedEventId}`);
    } else if (row.linkedProjectId) {
      navigate(`/projects/${row.linkedProjectId}`);
    } else if (row.source === 'performance_payment') {
      navigate('/performances');
    }
  }, [navigate]);

  const columns: ColumnDef<TransactionItem>[] = useMemo(() => [
    {
      key: 'date', label: 'Data', sortable: true,
      render: (row: TransactionItem) => (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text)', whiteSpace: 'nowrap', position: 'relative' }}>
          {formatDate(row.date)}
          {row.dateIsApproximate && (
            <span title="Data basata sull'ultima modifica del record, potrebbe non essere il giorno reale del pagamento" style={{ color: 'var(--color-warning)', marginLeft: 3, cursor: 'help' }}>⚠</span>
          )}
        </span>
      ),
    },
    {
      key: 'description', label: 'Descrizione', sortable: false,
      render: (row: TransactionItem) => (
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>{row.description}</span>
      ),
    },
    {
      key: 'amount', label: 'Importo', sortable: true,
      render: (row: TransactionItem) => (
        <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: row.amount >= 0 ? 'var(--color-success)' : 'var(--color-error)', whiteSpace: 'nowrap' }}>
          {formatCurrency(row.amount)}
        </span>
      ),
    },
    {
      key: 'type', label: 'Tipo', sortable: false,
      render: (row: TransactionItem) => (
        <span style={{
          display: 'inline-block', padding: '1px 8px', borderRadius: 'var(--radius-interactive)',
          fontSize: 10, fontWeight: 600,
          background: row.type === 'INCOME' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.10)',
          color: row.type === 'INCOME' ? '#22c55e' : '#ef4444',
        }}>{row.type === 'INCOME' ? 'Entrata' : 'Uscita'}</span>
      ),
    },
    {
      key: 'status', label: 'Stato', sortable: false,
      render: (row: TransactionItem) => (
        <span style={{
          display: 'inline-block', padding: '1px 8px', borderRadius: 'var(--radius-interactive)',
          fontSize: 10, fontWeight: 600,
          background: row.status === 'PAID' ? 'rgba(34,197,94,0.12)' : 'rgba(251,191,36,0.12)',
          color: row.status === 'PAID' ? '#22c55e' : '#fbbf24',
        }}>{row.status === 'PAID' ? 'Pagato' : 'In attesa'}</span>
      ),
    },
    {
      key: 'source', label: 'Origine', sortable: false,
      render: (row: TransactionItem) => (
        <span style={{
          display: 'inline-block', padding: '1px 8px', borderRadius: 'var(--radius-interactive)',
          fontSize: 10, fontWeight: 600,
          background: 'rgba(148,163,184,0.12)', color: '#94a3b8',
        }}>{SOURCE_LABELS[row.source] || row.source}</span>
      ),
    },
    {
      key: 'actions', label: '', sortable: false,
      render: (row: TransactionItem) => row.source === 'manual' ? (
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          <Button variant="ghost" size="sm" onClick={() => { const id = row.id.replace('manual_', ''); setEditing({ ...row, id }); setShowForm(true); }}>Modifica</Button>
          <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(row.id.replace('manual_', ''))} style={{ color: 'var(--color-error)' }}>Elimina</Button>
        </div>
      ) : null,
    },
  ], [editing]);

  const selectedIds = sel.selectedIds;
  const manualSelected = useMemo(() => {
    if (selectedIds.size === 0) return [];
    return Array.from(selectedIds).filter((id: string) => id.startsWith('manual_')).map((id: string) => id.replace('manual_', ''));
  }, [selectedIds]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <TimeRangeFilter from={filters.from} to={filters.to} onApply={(from, to) => handleFiltersChange({ from, to })} />
          <select value={filters.type} onChange={e => handleFiltersChange({ type: e.target.value as '' | 'INCOME' | 'EXPENSE' })}
            style={{ padding: '0.35rem 0.5rem', background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)', color: 'var(--color-text)', fontSize: 'var(--text-xs)' }}>
            <option value="">Entrate + Uscite</option>
            <option value="INCOME">Solo Entrate</option>
            <option value="EXPENSE">Solo Uscite</option>
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', cursor: 'pointer' }}>
            <input type="checkbox" checked={filters.showPending} onChange={e => handleFiltersChange({ showPending: e.target.checked })} />
            Includi attesi
          </label>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {manualSelected.length > 0 && (
            <Button variant="danger" size="sm" icon={<Trash2 size={12} />} onClick={() => {
              apiPost('/finance/transactions/bulk', { ids: manualSelected.map(id => `manual_${id}`) })
                .then(() => { qc.invalidateQueries({ queryKey: ['finance-transactions'] }); sel.setSelectedIds(new Set()); toast.success('Movimenti eliminati'); })
                .catch((e: Error) => toast.error(e.message));
            }}>Elimina {manualSelected.length}</Button>
          )}
          <Button size="sm" icon={<Plus size={12} />} onClick={() => { setEditing(null); setShowForm(true); }}>Nuovo movimento</Button>
        </div>
      </div>

      {isLoading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-faint)' }}>Caricamento...</div>
      ) : transactions.length === 0 ? (
        <EmptyState title="Nessun movimento" description="Prova a modificare i filtri o aggiungi un movimento manuale" />
      ) : (
        <DataTable
          columns={columns}
          data={transactions}
          gridColumns="1fr 2fr 1fr 80px 80px 100px 100px"
          selectable
          selectedIds={selectedIds}
          onSelectionChange={sel.setSelectedIds}
          rowKey={(r: TransactionItem) => r.id}
          onRowClick={handleRowClick}
        />
      )}

      {totals && (
        <div style={{
          position: 'sticky', bottom: 0, zIndex: 1,
          background: 'var(--glass-bg-card)', backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)',
          padding: '0.75rem 1rem', display: 'flex', gap: '2rem', justifyContent: 'center',
          flexWrap: 'wrap', fontSize: 'var(--text-sm)',
        }}>
          <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>
            Entrate: {formatCurrency(totals.income)}
          </span>
          <span style={{ color: 'var(--color-error)', fontWeight: 600 }}>
            Uscite: {formatCurrency(totals.expense)}
          </span>
          <span style={{ color: 'var(--color-text)', fontWeight: 700 }}>
            Saldo: {formatCurrency(totals.balance)}
          </span>
          {totals.projectedBalance !== undefined && (
            <span style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
              Proiezione con attesi: {formatCurrency(totals.projectedBalance)}
              {' '}(+{formatCurrency(totals.pendingIncome ?? 0)} entrate, {formatCurrency(totals.pendingExpense ?? 0)} uscite)
            </span>
          )}
        </div>
      )}

      {hasMore && (
        <div style={{ textAlign: 'center' }}>
          <Button variant="secondary" size="sm" onClick={() => setOffset(o => o + 200)} loading={isLoading}>
            Carica altri
          </Button>
        </div>
      )}

      <TransactionFormModal
        open={showForm}
        onClose={() => { setShowForm(false); setEditing(null); }}
        editing={editing}
        onSave={handleSave}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
        title="Elimina movimento"
        description="Sei sicuro di voler eliminare questo movimento?"
        confirmLabel="Elimina"
      />
    </div>
  );
}
